/**
 * R1 — Operator Inbox. The prioritised queue of changes since the
 * operator's last visit. Honesty rails (per CD spec):
 *
 *   1. ChangeBadge distinguishes real-world events from data-quality
 *      events. Data-quality items show "check the feed, not the
 *      outbreak" inline so the operator's first reaction matches the
 *      RIGHT action.
 *   2. "Needs attention" group at top = high-tier real-world changes.
 *      The rest are a Slack-style change feed with a "since last visit"
 *      divider so the operator can scan what's new.
 *   3. Persona lens drives sort + filter defaults (WHO duty / Nat. CMO /
 *      USG analyst); all controls remain available to everyone.
 *   4. Empty state for the change feed says "Nothing new since your last
 *      visit · the system is live — N signals tracked, none changed in
 *      this lens" — never silent.
 *
 * Per-item verbs: Acknowledge / Snooze / Pin (→ pins rail) / Dismiss.
 */

import { useMemo, useState } from 'react';
import {
  ChangeBadge, FamilyGlyph, IconBtn, TierPip, CovBars6, relTime,
} from './primitives';
import {
  CHANGE_META, PERSONA_META, type InboxItem, type Persona,
} from './types';

export interface InboxProps {
  persona: Persona;
  items: readonly InboxItem[];
  lastVisitHours: number;
  /** Caller wires this to add the item to the persistent pins rail. */
  onPin?: (item: InboxItem) => void;
}

export function Inbox({ persona, items: initialItems, lastVisitHours, onPin }: InboxProps) {
  const [items, setItems] = useState<readonly InboxItem[]>(initialItems);
  const personaMeta = PERSONA_META[persona];

  // Persona-driven sort + filter. All controls remain available — this is the default.
  const view = useMemo(() => {
    let v = items.filter(i => i.state !== 'dismissed' && i.state !== 'snoozed');
    if (persona === 'who') v = v.filter(i => i.region === 'AFRO' || i.region === 'EMRO');
    else if (persona === 'cmo') v = v.filter(i => i.iso3 === 'GBR'); // demo: deputy CMO = UK
    // usg = no filter (firehose)
    const rank = (i: InboxItem) => (i.tier === 0 ? 9 : i.tier);
    if (persona === 'cmo') v = [...v].sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts));
    else if (persona === 'usg') v = [...v].sort((a, b) =>
      (b.novel ? 1 : 0) - (a.novel ? 1 : 0) || rank(a) - rank(b));
    else v = [...v].sort((a, b) => rank(a) - rank(b) || Date.parse(b.ts) - Date.parse(a.ts));
    return v;
  }, [items, persona]);

  const lastVisitMs = Date.now() - lastVisitHours * 3_600_000;
  const attention = view.filter(i => i.tier <= 2 && CHANGE_META[i.change].real);
  const rest = view.filter(i => !(i.tier <= 2 && CHANGE_META[i.change].real));
  const firstOldIdx = rest.findIndex(i => Date.parse(i.ts) < lastVisitMs);

  const act = (id: string, state: InboxItem['state']) => setItems(items.map(i => i.id === id ? { ...i, state } : i));

  const sortLabel = persona === 'cmo' ? 'recency' : persona === 'usg' ? 'novelty + threat' : 'threat-first';
  const filterLabel = persona === 'who' ? 'region ∈ {AFRO, EMRO}' : persona === 'cmo' ? 'iso3 = own country' : 'none — full firehose';

  return (
    <div>
      <header style={{ marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 680 }}>Inbox</h2>
        <div style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, color: 'var(--bm-text-subtle)', marginTop: 3 }}>
          {personaMeta.name} · sort: {sortLabel} · filter: {filterLabel}
        </div>
      </header>

      {attention.length > 0 && (
        <section style={{ marginBottom: 16 }}>
          <h3 style={GROUP_LABEL_STYLE}>
            <span style={{ ...DOT_STYLE, background: 'var(--bm-threat-1)' }} />
            Needs attention · {attention.length}
          </h3>
          {attention.map(i => <Row key={i.id} item={i} onPin={onPin} onAct={act} />)}
        </section>
      )}

      <section>
        <h3 style={GROUP_LABEL_STYLE}>
          <span style={{ ...DOT_STYLE, background: 'var(--bm-text-subtle)' }} />
          Change feed
        </h3>
        {rest.length === 0 ? (
          <div style={EMPTY_STYLE}>
            <div style={{ fontWeight: 600 }}>Nothing new since your last visit</div>
            <div style={{ color: 'var(--bm-text-subtle)', fontSize: 13, marginTop: 3 }}>
              The system is live — {items.length} signals tracked, none changed in this lens.
            </div>
          </div>
        ) : (
          rest.map((i, idx) => (
            <div key={i.id}>
              {idx === firstOldIdx && firstOldIdx > 0 && (
                <UnreadDivider hours={lastVisitHours} />
              )}
              <Row item={i} onPin={onPin} onAct={act} />
            </div>
          ))
        )}
      </section>
    </div>
  );
}

const GROUP_LABEL_STYLE = {
  display: 'flex', alignItems: 'center', gap: 8,
  fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 600,
  letterSpacing: '.5px', textTransform: 'uppercase' as const,
  color: 'var(--bm-text-muted)',
  margin: '0 0 10px',
};

const DOT_STYLE = { display: 'inline-block', width: 8, height: 8, borderRadius: 999 };

const EMPTY_STYLE = {
  padding: '20px 14px', textAlign: 'center' as const,
  border: '1px dashed var(--bm-border)', borderRadius: 'var(--bm-radius-md)',
  fontSize: 13,
};

function UnreadDivider({ hours }: { hours: number }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      margin: '12px 0',
      fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 600, letterSpacing: '.4px',
      color: 'var(--bm-unread, var(--bm-text-muted))',
    }}>
      <span style={{ flex: 1, height: 1, background: 'var(--bm-unread, var(--bm-border))', opacity: 0.4 }} />
      <span style={{
        padding: '2px 9px', borderRadius: 999,
        background: 'var(--bm-unread-bg, var(--bm-bg-subtle))',
        border: '1px solid var(--bm-unread, var(--bm-border))',
      }}>Since your last visit · {hours}h ago</span>
      <span style={{ flex: 1, height: 1, background: 'var(--bm-unread, var(--bm-border))', opacity: 0.4 }} />
    </div>
  );
}

function Row({
  item, onPin, onAct,
}: { item: InboxItem; onPin?: (i: InboxItem) => void; onAct: (id: string, s: InboxItem['state']) => void }) {
  return (
    <div style={{
      display: 'flex', gap: 12,
      padding: '10px 12px',
      borderBottom: '1px solid var(--bm-border)',
      borderLeft: `3px solid ${COLOR_FOR_TIER[item.tier]}`,
      opacity: item.state === 'ack' ? 0.6 : 1,
    }}>
      <div style={{ paddingTop: 2 }}>
        <FamilyGlyph family={item.family} color={item.idc} size={16} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
          <TierPip tier={item.tier} small />
          <ChangeBadge kind={item.change} small />
          {item.covGap && <CovBars6 level={item.change === 'coverage' ? 0 : 1} />}
          <span style={{ marginLeft: 'auto', fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>
            {relTime(item.ts)}
          </span>
        </div>
        <div style={{ fontSize: 14, marginBottom: 3 }}>
          <b>{item.common}</b> · {item.admin}
          {item.unverified && (
            <span style={{
              marginLeft: 8, padding: '0 6px', borderRadius: 3,
              fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
              color: 'var(--bm-prov-unverified, #c08cff)',
              background: 'var(--bm-bg-subtle)',
              border: '1px solid var(--bm-prov-unverified, #c08cff)55',
            }}>unverified</span>
          )}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--bm-text-muted)', marginBottom: 4 }}>{item.detail}</div>
        <div style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>
          {item.dataQuality && (
            <span style={{ color: 'var(--bm-chg-degraded)' }}>
              ⚠ data-quality event — check the feed, not the outbreak ·
            </span>
          )} {item.source} · as of {item.asOf.slice(0, 10)}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
        <IconBtn icon="✓" label="Acknowledge" on={item.state === 'ack'} tone="var(--bm-prov-confirmed)"
          onClick={() => onAct(item.id, item.state === 'ack' ? 'new' : 'ack')} />
        <IconBtn icon="⏰" label="Snooze 8h" onClick={() => onAct(item.id, 'snoozed')} />
        <IconBtn icon="📌" label="Pin to watchlist" tone="var(--bm-pin)" onClick={() => onPin?.(item)} />
        <IconBtn icon="×" label="Dismiss" onClick={() => onAct(item.id, 'dismissed')} />
      </div>
    </div>
  );
}

const COLOR_FOR_TIER: Record<number, string> = {
  0: 'var(--bm-threat-0)',
  1: 'var(--bm-threat-1)',
  2: 'var(--bm-threat-2)',
  3: 'var(--bm-threat-3)',
  4: 'var(--bm-threat-4)',
};
